import { event as gaEvent, getContentGroup } from "@/lib/gtag";
import { createCtaEventId, normalizeCtaPlacement } from "@/lib/cta-analytics";
import {
  getInternalTrafficContext,
  INTERNAL_TRAFFIC_TYPE,
  isInternalTraffic,
} from "@/lib/internal-traffic";
import { hasConsent } from "@/lib/legal/consent";

export const CTA_EVENTS_ENDPOINT = "/api/cta-events";
export const JOURNEY_SESSION_STORAGE_KEY = "eclat_journey_session_id";
export const JOURNEY_VERSION = 2;

const UTILITY_PATH_PREFIXES = [
  "/study-plan-generator",
  "/study-places-near-me",
  "/libraries-near-me",
  "/community-club-study-rooms-near-me",
  "/late-night-study-places-near-me",
  "/study-cafes-near-me",
  "/study-rooms-and-pods-near-me",
  "/l1r5-calculator",
  "/elr2b2-calculator",
  "/uas70-calculator",
  "/bursary-eligibility-calculator",
] as const;

const DECISION_PATH_PREFIXES = [
  "/jae-cut-off-points",
  "/dsa-talent-areas",
] as const;

const CALENDAR_KEYWORDS = [
  "calendar",
  "school-holidays",
  "public-holidays",
  "march-school-holidays",
  "june-school-holidays",
  "september-school-holidays",
  "year-end-school-holidays",
] as const;

const STUDY_KEYWORDS = [
  "study-plan",
  "study-spots",
  "study-places",
  "study-rooms",
  "study-cafes",
  "libraries",
  "community-club-study",
  "late-night-study",
] as const;

const ADMISSIONS_KEYWORDS = [
  "integrated-programme",
  "how-to-get-into-ip",
  "cut-off",
  "cut-off-points",
  "jae",
  "igp",
  "posting",
  "subject-prerequisites",
  "70rp",
  "dsa",
] as const;

const NOTES_KEYWORDS = [
  "notes",
  "practical",
  "practicals",
  "experiment",
  "experiments",
  "syllabus",
] as const;

export type JourneyStage =
  | "awareness"
  | "resource"
  | "utility"
  | "decision"
  | "community"
  | "lead_intent"
  | "lead"
  | "exit";

export type JourneyEventParams = Record<string, unknown> & {
  attempt_number?: number;
  audit_run_id?: string;
  automation_source?: string;
  chapter_id?: string;
  commercial_intent?: string;
  content_group?: string;
  cta_family?: string;
  distance_band?: string;
  error_code?: 1 | 2 | 3;
  device_class?: "mobile" | "desktop";
  filter_id?: string;
  filter_value?: string;
  filters_used?: string;
  funding_type?: "all" | "bursaries" | "scholarships";
  finder_experience_version?: string;
  interaction_method?: string;
  location_attempt_id?: string;
  search_attempt_id?: string;
  search_query?: string;
  search_request_id?: string;
  location_latency_bucket?: string;
  map_ready_latency_bucket?: string;
  location_mode?: "none" | "geo" | "manual";
  matched_criteria?: number;
  place_category?: string;
  preference_state?: "present" | "absent" | "unknown";
  recommendation_count?: number;
  recommendation_position?: number;
  recommendation_ready_latency_bucket?: string;
  recommendation_set_reason?: string;
  recommendation_set_sequence?: number;
  refinement_used?: boolean;
  result_end_position?: number;
  result_ids?: string[];
  result_origin?: "fresh" | "interaction" | "restored" | "url";
  result_position?: number;
  result_start_position?: number;
  result_count?: number;
  search_state?: "empty" | "matching" | "zero_results";
  section?: string;
  section_id?: string;
  spot_region?: string;
  spot_id?: string;
  study_need?: string;
  finder_visitor_status?: "new" | "returning" | "unknown";
  visit_status?: string;
  event_id?: string;
  is_primary_cta?: boolean | number | string;
  inbound_source_code?: string;
  journey_name?: string;
  journey_session_id?: string;
  journey_version?: number;
  lead_ref?: string;
  placement?: string;
  referrer_path?: string;
  source_cluster?: string;
  source_page?: string;
  source_stage?: JourneyStage | string;
  source_surface?: string;
  target_cluster?: string;
  target_id?: string;
  target_stage?: JourneyStage | string;
  target_type?: string;
  target_url?: string;
  telemetry_schema_version?: number;
  total_criteria?: number;
  selection_state?: "select" | "deselect";
  visible_count?: number;
  visible_result_count?: number;
  previous_visible_count?: number;
  position?: number;
  search_ui_variant?: string;
  search_input_mode?: string;
  guided_chip_count?: number;
  time_to_first_action_bucket?: string;
  close_reason?: string;
  search_attempt_outcome?: string;
};

type JourneyEventPayload = JourneyEventParams & {
  content_group?: string;
  event: string;
  event_id: string;
  journey_session_id?: string;
  journey_version: number;
  page_path?: string;
  page_title?: string;
  referrer_path?: string;
  server_path?: string;
  timestamp: string;
  traffic_type?: string;
  url?: string;
};

/**
 * Payload keys stripped from the GA4 copy of a journey event. GA4 caps events
 * at 25 developer-set params and the full journey bag rides that limit, which
 * risks silently dropping the conditional params (target_cluster,
 * referrer_path, is_primary_cta). Each key here either duplicates a field GA4
 * records natively or exists only for the first-party JSONL record, which
 * always receives the full payload.
 */
const GA4_REDUNDANT_KEYS = [
  "event", // = GA4 event_name
  "timestamp", // = GA4 event_timestamp
  "url", // = GA4 page_location
  "page_path", // = GA4 page_location (path component)
  "page_title", // GA4 collects page_title natively
  "server_path", // first-party sink field; equals page_path
  "action", // legacy UA taxonomy; unregistered in GA4
  "value", // legacy UA taxonomy; GA4 reserves value for numeric metrics
  "event_id", // first-party idempotency key; too high-cardinality for GA4
  "location_attempt_id", // first-party correlation key; too high-cardinality for GA4
  "search_attempt_id", // first-party correlation key; too high-cardinality for GA4
  "search_query", // first-party product research text; never send to GA4
  "search_request_id", // first-party operational correlation; too high-cardinality for GA4
  "lead_ref", // first-party CRM join key; never send to GA4
  "inbound_source_code", // first-party/operator attribution; never send to GA4
  "result_ids", // first-party result identity batch; too high-cardinality for GA4
] as const;

function normalizePrimaryCtaFlag(
  value: JourneyEventParams["is_primary_cta"]
): number | undefined {
  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 0 ? 1 : 0;
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  if (!normalized) return undefined;
  if (normalized === "1" || normalized === "true" || normalized === "yes") {
    return 1;
  }
  if (normalized === "0" || normalized === "false" || normalized === "no") {
    return 0;
  }

  return undefined;
}

function getCurrentPathname(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const pathname = window.location.pathname?.trim();
  return pathname || undefined;
}

function includesAnyKeyword(
  value: string,
  keywords: readonly string[]
): boolean {
  return keywords.some((keyword) => value.includes(keyword));
}

function inferJourneyStageFromPath(
  pathname: string | undefined
): string | undefined {
  const normalizedPath = pathname?.trim().toLowerCase();
  if (!normalizedPath) return undefined;

  if (normalizedPath === "/") return "awareness";

  if (
    UTILITY_PATH_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix))
  ) {
    return "utility";
  }

  if (
    DECISION_PATH_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix))
  ) {
    return "decision";
  }

  if (
    normalizedPath.startsWith("/landing/") ||
    normalizedPath.startsWith("/locations/") ||
    normalizedPath.startsWith("/careers") ||
    normalizedPath.startsWith("/support") ||
    normalizedPath.startsWith("/media") ||
    normalizedPath.startsWith("/partnerships") ||
    normalizedPath.startsWith("/media-partnerships")
  ) {
    return "decision";
  }

  if (normalizedPath.startsWith("/blog/")) {
    const slug = normalizedPath.split("/").pop() ?? normalizedPath;

    if (includesAnyKeyword(slug, ADMISSIONS_KEYWORDS)) {
      return "decision";
    }

    if (includesAnyKeyword(slug, NOTES_KEYWORDS)) {
      return "resource";
    }

    return "awareness";
  }

  return "awareness";
}

function inferJourneyClusterFromPath(
  pathname: string | undefined
): string | undefined {
  const normalizedPath = pathname?.trim().toLowerCase();
  if (!normalizedPath) return undefined;

  if (normalizedPath.startsWith("/careers")) return "careers";
  if (normalizedPath.startsWith("/landing/")) return "landing";

  if (
    UTILITY_PATH_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix)) ||
    includesAnyKeyword(normalizedPath, STUDY_KEYWORDS)
  ) {
    return "study";
  }

  if (
    DECISION_PATH_PREFIXES.some((prefix) => normalizedPath.startsWith(prefix))
  ) {
    return "admissions";
  }

  if (normalizedPath.startsWith("/blog/")) {
    const slug = normalizedPath.split("/").pop() ?? normalizedPath;

    if (includesAnyKeyword(slug, CALENDAR_KEYWORDS)) {
      return "calendar";
    }

    if (includesAnyKeyword(slug, STUDY_KEYWORDS)) {
      return "study";
    }

    if (includesAnyKeyword(slug, ADMISSIONS_KEYWORDS)) {
      return "admissions";
    }

    if (includesAnyKeyword(slug, NOTES_KEYWORDS)) {
      return "notes";
    }
  }

  return undefined;
}

function resolveInternalPathname(url: string | undefined): string | undefined {
  const trimmed = url?.trim();
  if (!trimmed) return undefined;

  try {
    const base =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://eclatinstitute.sg";
    const resolved = new URL(trimmed, base);
    const currentHostname =
      typeof window !== "undefined" ? window.location.hostname : "";
    const isInternal =
      resolved.hostname === currentHostname ||
      resolved.hostname === "eclat.local" ||
      resolved.hostname.endsWith("eclatinstitute.sg");

    return isInternal ? resolved.pathname : undefined;
  } catch {
    return undefined;
  }
}

function normalizeSourcePage(pathname: string | undefined): string | undefined {
  if (!pathname) return undefined;
  if (pathname === "/") return "home";
  return pathname.replace(/^\/+/, "");
}

function getCurrentReferrerPath(): string | undefined {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return undefined;
  }

  const referrer = document.referrer?.trim();
  if (!referrer) return undefined;

  try {
    const referrerUrl = new URL(referrer);
    if (referrerUrl.origin !== window.location.origin) {
      return undefined;
    }
    return referrerUrl.pathname;
  } catch {
    return undefined;
  }
}

export function getJourneySessionId(): string | null {
  if (typeof window === "undefined") return null;

  try {
    const existing = window.sessionStorage.getItem(JOURNEY_SESSION_STORAGE_KEY);
    if (existing) return existing;

    const generated =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `journey-${Date.now()}-${Math.random().toString(16).slice(2)}`;

    window.sessionStorage.setItem(JOURNEY_SESSION_STORAGE_KEY, generated);
    return generated;
  } catch {
    return null;
  }
}

function buildJourneyPayload(
  eventName: string,
  params: JourneyEventParams
): JourneyEventPayload {
  const pagePath = getCurrentPathname();
  const contentGroup =
    typeof params.content_group === "string" &&
    params.content_group.trim().length > 0
      ? params.content_group
      : pagePath
      ? getContentGroup(pagePath)
      : undefined;
  const referrerPath =
    typeof params.referrer_path === "string" &&
    params.referrer_path.trim().length > 0
      ? params.referrer_path
      : getCurrentReferrerPath();
  const sourcePage =
    typeof params.source_page === "string" &&
    params.source_page.trim().length > 0
      ? params.source_page
      : normalizeSourcePage(pagePath);
  const sourceSurface =
    typeof params.source_surface === "string"
      ? normalizeCtaPlacement(params.source_surface)
      : undefined;
  const sourceStage =
    typeof params.source_stage === "string" &&
    params.source_stage.trim().length > 0
      ? params.source_stage
      : inferJourneyStageFromPath(pagePath);
  const sourceCluster =
    typeof params.source_cluster === "string" &&
    params.source_cluster.trim().length > 0
      ? params.source_cluster
      : inferJourneyClusterFromPath(pagePath);
  const targetPath = resolveInternalPathname(
    typeof params.target_url === "string" ? params.target_url : undefined
  );
  const targetCluster =
    typeof params.target_cluster === "string" &&
    params.target_cluster.trim().length > 0
      ? params.target_cluster
      : inferJourneyClusterFromPath(targetPath);
  const journeySessionId =
    typeof params.journey_session_id === "string" &&
    params.journey_session_id.trim().length > 0
      ? params.journey_session_id
      : getJourneySessionId() ?? undefined;
  const isPrimaryCta = normalizePrimaryCtaFlag(params.is_primary_cta);

  return {
    ...params,
    ...(contentGroup ? { content_group: contentGroup } : {}),
    event: eventName,
    event_id:
      typeof params.event_id === "string" && params.event_id.trim().length > 0
        ? params.event_id.trim()
        : createCtaEventId(),
    ...(isPrimaryCta !== undefined ? { is_primary_cta: isPrimaryCta } : {}),
    ...(journeySessionId ? { journey_session_id: journeySessionId } : {}),
    journey_version:
      typeof params.journey_version === "number" &&
      Number.isFinite(params.journey_version)
        ? params.journey_version
        : JOURNEY_VERSION,
    ...(pagePath ? { page_path: pagePath } : {}),
    ...(typeof document !== "undefined" && document.title
      ? { page_title: document.title }
      : {}),
    ...(referrerPath ? { referrer_path: referrerPath } : {}),
    ...(sourceCluster ? { source_cluster: sourceCluster } : {}),
    ...(sourcePage ? { source_page: sourcePage } : {}),
    ...(sourceSurface ? { source_surface: sourceSurface } : {}),
    ...(sourceStage ? { source_stage: sourceStage } : {}),
    server_path: pagePath,
    ...(targetCluster ? { target_cluster: targetCluster } : {}),
    timestamp: new Date().toISOString(),
    ...(typeof window !== "undefined"
      ? { url: `${window.location.origin}${window.location.pathname}` }
      : {}),
  };
}

/**
 * Fire a journey-aware CTA event to both GA4 and the server-side JSONL log.
 * This helper is consent-aware and must never throw.
 */
export function trackJourneyEvent(
  eventName: string,
  params: JourneyEventParams = {}
): void {
  try {
    if (!hasConsent("analytics")) return;
  } catch {
    return;
  }

  // A browser flagged as internal/team traffic keeps writing to the first-party
  // sink (stamped traffic_type=internal so it can be filtered downstream) but is
  // kept out of GA4 entirely. Skipping gaEvent also avoids leaking events into
  // gtag's pendingEvents queue, which never flushes when GA4 is never loaded.
  let internal = false;
  let internalContext: ReturnType<typeof getInternalTrafficContext> = {};
  try {
    internal = isInternalTraffic();
    internalContext = getInternalTrafficContext();
  } catch {
    // Storage restrictions should not break analytics callers.
  }

  let payload: JourneyEventPayload;
  try {
    payload = buildJourneyPayload(eventName, params);
  } catch {
    return;
  }
  if (internal) {
    payload.traffic_type = INTERNAL_TRAFFIC_TYPE;
    if (internalContext.automationSource) {
      payload.automation_source = internalContext.automationSource;
    }
    if (internalContext.auditRunId) {
      payload.audit_run_id = internalContext.auditRunId;
    }
  }

  if (!internal) {
    const gaParams: Record<string, unknown> = { ...payload };
    for (const key of GA4_REDUNDANT_KEYS) {
      delete gaParams[key];
    }
    if (typeof window !== "undefined") {
      gaParams.page_location = `${window.location.origin}${window.location.pathname}`;
      gaParams.page_referrer = getCurrentReferrerPath();
    }
    try {
      gaEvent(eventName, gaParams);
    } catch {
      // Keep the first-party delivery independent from GA4 failures.
    }
  }

  if (typeof window === "undefined") return;

  let body: string;
  try {
    body = JSON.stringify(payload);
  } catch {
    return;
  }

  let deliveredByBeacon = false;
  try {
    if (typeof navigator.sendBeacon === "function") {
      deliveredByBeacon = navigator.sendBeacon(
        CTA_EVENTS_ENDPOINT,
        new Blob([body], { type: "application/json" })
      );
    }
  } catch {
    // Fall through to fetch when the browser rejects beacon delivery.
  }

  if (deliveredByBeacon) return;

  try {
    void Promise.resolve(
      fetch(CTA_EVENTS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      })
    ).catch(() => {});
  } catch {
    // Analytics must never break user actions.
  }
}
